<template>
  <div v-if="entries.length" class="dict-bars">
    <div v-for="[k, v] in visible" :key="k" class="dict-row">
      <span class="dict-label">{{ k }}</span>
      <div class="dict-track">
        <div class="dict-fill" :style="{ width: widthFor(v) }" />
      </div>
      <span class="dict-value mono">{{ format(v) }}</span>
    </div>
  </div>
  <div v-else class="metric-value composite">—</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { fmtInt, fmtNum } from "@/lib/format";

const props = withDefaults(
  defineProps<{
    dict: Record<string, number>;
    unit?: string;
    /** Cap rows shown; v1 caps at 5. */
    maxRows?: number;
  }>(),
  { unit: "", maxRows: 5 },
);

const entries = computed<[string, number][]>(() => {
  return Object.entries(props.dict).filter(
    (kv): kv is [string, number] => typeof kv[1] === "number",
  );
});

const visible = computed(() => entries.value.slice(0, props.maxRows));

const peakAbs = computed(() => {
  let m = 1;
  for (const [, v] of entries.value) {
    const a = Math.abs(v);
    if (a > m) m = a;
  }
  return m;
});

function widthFor(v: number): string {
  return `${((Math.abs(v) / peakAbs.value) * 100).toFixed(1)}%`;
}

function format(v: number): string {
  return props.unit === "d" ? `${fmtNum(v, 1)}d` : fmtInt(v);
}
</script>

<style scoped>
.dict-bars { display: flex; flex-direction: column; gap: 4px; margin-top: 4px; }
.dict-row {
  display: grid; grid-template-columns: minmax(60px, 1fr) 1fr 60px;
  align-items: center; gap: 8px; font-size: 12px;
}
.dict-label {
  color: var(--text-muted, #6b7280);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.dict-track { background: var(--surface-section, #f3f4f6); height: 6px; border-radius: 3px; overflow: hidden; }
.dict-fill { background: var(--primary-color, #2563eb); height: 100%; }
.dict-value { text-align: right; color: var(--text-muted, #6b7280); }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.metric-value.composite { font-size: 16px; color: var(--text-muted, #6b7280); }
</style>
